<template>
  <div class="kv-ve-corners">
    <!-- Premade options (always visible on top) -->
    <div class="kv-ve-corners__presets">
      <button
        v-for="opt in limitedOptions"
        :key="opt.value"
        type="button"
        class="kv-ve-corners__btn"
        :class="{ active: isPresetActive(opt.value) }"
        :title="opt.title"
        @click="set(opt.value)"
      >
        <span class="kv-ve-corners__icon" :style="iconStyle(opt.value/4)"></span>
      </button>
      <!-- Cog button to enter custom mode -->
      <button
        type="button"
        class="kv-ve-corners__btn kv-ve-corners__btn--cog"
        :class="{ active: isCustomMode }"
        :title="t('visual_editor-custom_settings')"
        @click="toggleCustomMode"
      >
        <Icon icon-class="ui-settings-02" size="4" />
      </button>
    </div>

    <!-- Custom mode: 4 individual fields (shown below presets when active) -->
    <div v-if="isCustomMode" class="kv-ve-corners__custom">
      <!-- Uniform slider + input -->
      <div class="kv-ve-corners__uniform">
        <div class="kv-ve-corners__field">
          <span class="kv-ve-corners__corner-icon kv-ve-corners__corner-icon--all"></span>
          <input
            type="text"
            class="kv-ve-corners__input"
            :value="uniformInputDisplay"
            @input="onUniformInput($event.target.value)"
          />
        </div>
        <input
          type="range"
          class="kv-ve-corners__slider"
          :value="uniformInputForSlider"
          min="0"
          max="600"
          @input="onUniformSlider($event.target.value)"
        />
      </div>

      <!-- Individual corner fields -->
      <div class="kv-ve-corners__corners">
        <div class="kv-ve-corners__field">
          <span class="kv-ve-corners__corner-icon kv-ve-corners__corner-icon--top-left"></span>
          <input
            type="number"
            class="kv-ve-corners__input"
            :value="customValues.topLeft"
            min="0"
            @input="setCorner('topLeft', $event.target.value)"
          />
        </div>
        <div class="kv-ve-corners__field">
          <span class="kv-ve-corners__corner-icon kv-ve-corners__corner-icon--top-right"></span>
          <input
            type="number"
            class="kv-ve-corners__input"
            :value="customValues.topRight"
            min="0"
            @input="setCorner('topRight', $event.target.value)"
          />
        </div>
        <div class="kv-ve-corners__field">
          <span class="kv-ve-corners__corner-icon kv-ve-corners__corner-icon--bottom-left"></span>
          <input
            type="number"
            class="kv-ve-corners__input"
            :value="customValues.bottomLeft"
            min="0"
            @input="setCorner('bottomLeft', $event.target.value)"
          />
        </div>
        <div class="kv-ve-corners__field">
          <span class="kv-ve-corners__corner-icon kv-ve-corners__corner-icon--bottom-right"></span>
          <input
            type="number"
            class="kv-ve-corners__input"
            :value="customValues.bottomRight"
            min="0"
            @input="setCorner('bottomRight', $event.target.value)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from "@/components/icon/icon.vue";

export default {
  name: "VeCornersPicker",
  components: { Icon },
  props: {
    value: { type: [Number, String, Object], default: 0 },
    options: { type: Array, default: () => [] },
  },
  data() {
    return {
      isCustomMode: false,
      customValues: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0,
      },
    };
  },
  computed: {
    // Show first 5 options (0, 4, 8, 16, 32) to fit in one line before cog
    limitedOptions() {
      return this.options.slice(0, 6);
    },
    uniformInputDisplay() {
      const vals = Object.values(this.customValues);
      const allSame = vals.every((v) => v === vals[0]);
      return allSame ? vals[0] : "-";
    },
    uniformInputForSlider() {
      const vals = Object.values(this.customValues);
      const allSame = vals.every((v) => v === vals[0]);
      // When different, use average for slider position, but display will show "-"
      if (allSame) return vals[0];
      const avg = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
      return avg;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        // Detect if value is custom object
        if (newVal && typeof newVal === "object" && !Array.isArray(newVal) && newVal.topLeft !== undefined) {
          // Only update if values actually changed to avoid loops
          const newTopLeft = Number(newVal.topLeft) || 0;
          const newTopRight = Number(newVal.topRight) || 0;
          const newBottomLeft = Number(newVal.bottomLeft) || 0;
          const newBottomRight = Number(newVal.bottomRight) || 0;
          
          if (
            this.customValues.topLeft !== newTopLeft ||
            this.customValues.topRight !== newTopRight ||
            this.customValues.bottomLeft !== newBottomLeft ||
            this.customValues.bottomRight !== newBottomRight
          ) {
            this.isCustomMode = true;
          this.customValues = {
            topLeft: newTopLeft,
            topRight: newTopRight,
            bottomLeft: newBottomLeft,
            bottomRight: newBottomRight,
          };
          } else if (!this.isCustomMode) {
            this.isCustomMode = true;
          }
        } else if (typeof newVal === "number" || typeof newVal === "string") {
          // If switching from custom to premade, exit custom mode
          if (this.isCustomMode) {
            this.isCustomMode = false;
          }
        }
      },
    },
    customValues: {
      deep: true,
      handler() {
        // Values changed, computed properties will update automatically
      },
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    isPresetActive(optValue) {
      if (this.isCustomMode) return false;
      return Number(this.value) === Number(optValue);
    },
    set(v) {
      // Exit custom mode and set preset value
      this.isCustomMode = false;
      const n = Number(v);
      this.$emit("input", Number.isFinite(n) ? n : 0);
    },
    toggleCustomMode() {
      if (this.isCustomMode) {
        // Exit custom mode, use topLeft value
        this.isCustomMode = false;
        const topLeft = Number(this.customValues.topLeft) || 0;
        this.$emit("input", topLeft);
      } else {
        // Enter custom mode, initialize from current value
        const current = typeof this.value === "number" ? this.value : 0;
        this.customValues = {
          topLeft: current,
          topRight: current,
          bottomLeft: current,
          bottomRight: current,
        };
        this.isCustomMode = true;
        this.emitCustom();
      }
    },
    updateUniformInput() {
      // This is called when customValues change, no need to update uniformInput anymore
    },
    onUniformInput(val) {
      // When user types in the input field, apply to all corners
      const n = Math.max(0, Number(val) || 0);
      if (Number.isFinite(n)) {
        this.applyUniformValue(n);
      }
    },
    onUniformSlider(val) {
      // When user moves slider, immediately apply to all corners
      const n = Math.max(0, Number(val) || 0);
      if (Number.isFinite(n)) {
        this.applyUniformValue(n);
      }
    },
    applyUniformValue(n) {
      this.customValues = {
        topLeft: n,
        topRight: n,
        bottomLeft: n,
        bottomRight: n,
      };
      this.emitCustom();
    },
    setCorner(corner, val) {
      const n = Math.max(0, Number(val) || 0);
      this.customValues[corner] = n;
      this.emitCustom();
    },
    applyUniform() {
      // Apply current uniform input value to all corners
      const n = Math.max(0, Number(this.uniformInputForSlider) || 0);
      this.applyUniformValue(n);
    },
    emitCustom() {
      this.$emit("input", { ...this.customValues });
    },
    iconStyle(v) {
      const n = Number(v);
      const isCircle = Number.isFinite(n) && n >= 100;
      const px = Number.isFinite(n) ? Math.max(0, Math.min(n, 12)) : 0;
      if (isCircle) {
        return { borderRadius: "999px" };
      }
      // Illustrate ONLY the top-left corner as requested
      return { borderTopLeftRadius: `${px}px` };
    },
  },
};
</script>

<style scoped lang="less">
.kv-ve-corners {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Presets row (always visible) */
.kv-ve-corners__presets {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
}
.kv-ve-corners__btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all 0.12s ease;
  flex: 0 0 auto;
}
.kv-ve-corners__btn:hover:not(:disabled) {
  background: rgba(42, 170, 76, 0.08);
  border-color: rgba(42, 170, 76, 0.35);
}
.kv-ve-corners__btn.active {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.14);
  background: rgba(42, 170, 76, 0.10);
}
.kv-ve-corners__btn--cog {
  color: #344054;
}
.kv-ve-corners__btn--cog.active {
  color: #027a48;
}
.kv-ve-corners__btn--apply {
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
}
.kv-ve-corners__icon {
  width: 16px;
  height: 16px;
  border: 2px solid #98a2b3;
  background: transparent;
}

/* Custom mode */
.kv-ve-corners__custom {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}
.kv-ve-corners__uniform {
  display: flex;
  align-items: center;
  gap: 8px;
}
.kv-ve-corners__corners {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.kv-ve-corners__field {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  min-width: 0;
}
.kv-ve-corners__corner-icon {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
  display: inline-block;
}
.kv-ve-corners__input {
  flex: 1;
  border: 0;
  background: transparent;
  font-size: 12px;
  color: #344054;
  outline: none;
  min-width: 0;
  width: 40px;
  text-align: right;
  padding: 0;
}
.kv-ve-corners__input::-webkit-inner-spin-button,
.kv-ve-corners__input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.kv-ve-corners__input[type="number"] {
  -moz-appearance: textfield;
}
.kv-ve-corners__slider {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
  min-width: 60px;
}
.kv-ve-corners__slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.1);
}
.kv-ve-corners__slider::-webkit-slider-thumb:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.14);
}
.kv-ve-corners__slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.1);
}
.kv-ve-corners__slider::-moz-range-thumb:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.14);
}

/* Corner icons (CSS-based) */
.kv-ve-corners__corner-icon {
  border: 2px dashed #98a2b3;
  background: transparent;
  position: relative;
}
.kv-ve-corners__corner-icon--all {
  border-style: solid;
  border-width: 2px;
}
.kv-ve-corners__corner-icon--top-left {
  border-top: 2px solid #98a2b3;
  border-left: 2px solid #98a2b3;
  border-right: 0;
  border-bottom: 0;
}
.kv-ve-corners__corner-icon--top-right {
  border-top: 2px solid #98a2b3;
  border-right: 2px solid #98a2b3;
  border-left: 0;
  border-bottom: 0;
}
.kv-ve-corners__corner-icon--bottom-left {
  border-bottom: 2px solid #98a2b3;
  border-left: 2px solid #98a2b3;
  border-top: 0;
  border-right: 0;
}
.kv-ve-corners__corner-icon--bottom-right {
  border-bottom: 2px solid #98a2b3;
  border-right: 2px solid #98a2b3;
  border-top: 0;
  border-left: 0;
}
</style>


